import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { BoletoEligibilityResponse } from '../services/api/boleto.service';

interface RecreateBoletoButtonProps {
  invoiceId: string;
  className?: string;
  style?: React.CSSProperties;
  onBoletoCreated?: (boletoData: any) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
}

/**
 * Recreate Boleto Button Component
 * 
 * This button allows users to recreate/regenerate a boleto for an invoice.
 * It will always force create a new boleto, even if one already exists.
 * This is useful when:
 * - The existing boleto has expired
 * - There are issues with the current boleto
 * - A new boleto is needed for any reason
 */
const RecreateBoletoButton: React.FC<RecreateBoletoButtonProps> = ({
  invoiceId,
  className = 'btn btn-warning',
  style,
  onBoletoCreated,
  onError,
  disabled = false
}) => {
  const [eligibility, setEligibility] = useState<BoletoEligibilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showDueDateInput, setShowDueDateInput] = useState(false);
  const [selectedDueDate, setSelectedDueDate] = useState<string>('');

  
  const checkEligibility = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.checkBoletoEligibility(invoiceId);
      setEligibility(response);
    } catch (error: any) {
      console.error('Error checking boleto eligibility:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to check boleto eligibility';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, onError]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  useEffect(() => {
    // Set default due date when eligibility is loaded
    if (eligibility?.existing_boleto?.boleto_duedate) {
      // Use existing boleto due date as default
      const existingDate = new Date(eligibility.existing_boleto.boleto_duedate);
      setSelectedDueDate(existingDate.toISOString().split('T')[0]);
    } else {
      // Default to 5 days from now if no existing boleto
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 5);
      setSelectedDueDate(defaultDate.toISOString().split('T')[0]);
    }
  }, [eligibility]);

  
  const handleButtonClick = () => {
    // Show the date input dialog
    setShowDueDateInput(true);
  };

  const handleConfirmRecreate = async () => {
    try {
      setCreating(true);
      setShowDueDateInput(false);

      // Validate that we have a due date
      if (!selectedDueDate) {
        throw new Error('Due date is required but not set');
      }

      // Convert selected date to datetime with time component
      const dueDateTime = new Date(selectedDueDate + 'T12:00:00').toISOString();

      // Log the due date information for debugging
      console.log('=== BOLETO CREATION DEBUG INFO ===');
      console.log('Invoice ID:', invoiceId);
      console.log('Selected Due Date (input):', selectedDueDate);
      console.log('Due Date Time (ISO):', dueDateTime);
      console.log('Force Create:', true);
      console.log('Existing Boleto Due Date:', eligibility?.existing_boleto?.boleto_duedate);
      console.log('===================================');

      // Always force create - this is the key feature of this button
      const response = await apiService.createBoletoForInvoice(invoiceId, undefined, true, dueDateTime);

      console.log('=== BOLETO CREATION RESPONSE ===');
      console.log('Response:', response);
      console.log('================================');

      if ((response as any)?.success) {
        // Success notification
        const message = eligibility?.existing_boleto
          ? 'Boleto recreated successfully! A new boleto has been generated.'
          : 'Boleto created successfully!';

        alert(message);
        onBoletoCreated?.(response);

        // Refresh eligibility to show the new boleto
        await checkEligibility();
      } else {
        throw new Error(response.message || 'Failed to recreate boleto');
      }
    } catch (error: any) {
      console.error('=== BOLETO CREATION ERROR ===');
      console.error('Error recreating boleto:', error);
      console.error('Error response data:', error.response?.data);
      console.error('=============================');
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to recreate boleto';
      alert(`Error: ${errorMessage}`);
      onError?.(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleCancelRecreate = () => {
    setShowDueDateInput(false);
  };

  // Show loading state
  if (loading) {
    return (
      <button
        className={`${className} disabled`}
        style={style}
        disabled={true}
      >
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Checking...
      </button>
    );
  }

  // Show not available if not eligible
  if (!eligibility?.eligible) {
    const reason = !eligibility?.currency_is_brl 
      ? 'Not BRL currency'
      : !eligibility?.customer_has_tax_id 
      ? 'No customer tax ID'
      : 'Not eligible';

    return (
      <button
        className={`${className} disabled`}
        style={style}
        disabled
        title={`Boleto recreation not available: ${reason}`}
      >
        🔄 Recreate N/A
      </button>
    );
  }

  // Get button configuration based on state
  const getButtonConfig = () => {
    if (creating) {
      return {
        text: 'Recreating...',
        title: 'Creating new boleto...',
        disabled: true,
        icon: <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      };
    }

    if (eligibility?.existing_boleto) {
      return {
        text: '🔄 Recreate Boleto',
        title: `Recreate boleto (current status: ${eligibility.existing_boleto.status})`,
        disabled: disabled,
        icon: null
      };
    }

    return {
      text: '🔄 Create Boleto',
      title: 'Create new boleto payment for this invoice',
      disabled: disabled,
      icon: null
    };
  };

  const buttonConfig = getButtonConfig();

  return (
    <div className="recreate-boleto-container">
      <button
        className={className}
        style={style}
        onClick={handleButtonClick}
        disabled={buttonConfig.disabled || creating}
        title={buttonConfig.title}
      >
        {buttonConfig.icon}
        {buttonConfig.text}
      </button>
      
      {/* Show existing boleto info if available */}
      {eligibility?.existing_boleto && (
        <div className="mt-1">
          <small className="text-muted">
            Current: {eligibility.existing_boleto.status}
            {eligibility.existing_boleto.created_at && (
              <span> • {new Date(eligibility.existing_boleto.created_at).toLocaleDateString()}</span>
            )}
            {eligibility.existing_boleto.boleto_duedate && (
              <span> • Due: {new Date(eligibility.existing_boleto.boleto_duedate).toLocaleDateString()}</span>
            )}
          </small>
          <br />
          <small className="text-warning">
            ⚠️ This will create a NEW boleto (existing one will remain)
          </small>
        </div>
      )}

      {/* Due Date Input Modal */}
      {showDueDateInput && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Set Boleto Due Date</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCancelRecreate}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="dueDateInput" className="form-label">
                    Due Date for New Boleto
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="dueDateInput"
                    value={selectedDueDate}
                    onChange={(e) => setSelectedDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <div className="form-text">
                    {eligibility?.existing_boleto?.boleto_duedate ? (
                      <>Current boleto due date: {new Date(eligibility.existing_boleto.boleto_duedate).toLocaleDateString()}</>
                    ) : (
                      <>Default: 5 days from today</>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCancelRecreate}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-warning" 
                  onClick={handleConfirmRecreate}
                  disabled={!selectedDueDate}
                >
                  🔄 Recreate Boleto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecreateBoletoButton;